import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Grid,
  Alert,
  Stack,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import { useSnackbar } from "notistack";
import api from "../../api/api";
import usePortalChat from '../../../hooks/usePortalChat';
import ClientRentalHistory from "./ClientRentalHistory";
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  ChatBubbleOutline as ChatIcon,
} from "@mui/icons-material";

const dateOnly = value => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}(?:T|$)/.test(value)
  && !Number.isNaN(new Date(value).getTime()) ? value.split('T')[0] : '';

const ClientsEditPage = () => {
  const { id } = useParams();
  const { openChat, opening: chatOpening, error: chatError } = usePortalChat(id);
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const isNewClient = id === "new";
  const copyData = location.state?.copyData;

  // Получаем параметр возврата из URL
  const searchParams = new URLSearchParams(location.search);
  const returnTo = searchParams.get("returnTo");
  const [formData, setFormData] = useState({
    name: "",
    contact_info: "",
    debt: 0,
    black_list: false,
    attraction_source_id: "",
    assistant_status: 1,
    // company_id: не отправляем, бэкенд сам определит по текущему пользователю

    // Новые поля
    client_type: "individual",
    first_name: "",
    last_name: "",
    middle_name: "",
    birth_date: "",
    gender: "",
    comment: "",

    // Паспортные данные
    passport_series: "",
    passport_number: "",
    passport_issued_by: "",
    passport_issued_date: "",
    passport_division_code: "",
    inn: "",

    // Дополнительные флаги
    no_contract: false,
    no_deposit: false,
    sms_notifications: true,

    // Контакты и адрес
    phone: "",
    email: "",
    address: "",

    // Скидки
    discount_percent: 0.0,
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [companies, setCompanies] = useState([]);
  const [attractionSources, setAttractionSources] = useState([]);
  const [errors, setErrors] = useState({});

  // Состояние для диалога создания источника привлечения
  const [sourceDialogOpen, setSourceDialogOpen] = useState(false);
  const [newSourceName, setNewSourceName] = useState("");
  const [newSourceDescription, setNewSourceDescription] = useState("");
  const [sourceSaving, setSourceSaving] = useState(false);
  // Функция для загрузки данных клиента
  const fetchClient = async () => {
    if (isNewClient) {
      // Получаем информацию о текущем пользователе
      const currentUser = await fetchCurrentUser();

      // Если есть данные для копирования, используем их
      if (copyData) {
        setFormData((previous) => ({
          ...previous,
          ...copyData,
          client_type: copyData.client_type || 'individual',
          birth_date: dateOnly(copyData.birth_date),
          passport_issued_date: dateOnly(copyData.passport_issued_date),
          company_id: currentUser.company_id, // Используем company_id текущего пользователя
          debt: 0, // Сбрасываем долг при копировании
        }));
      } else {
        // Для нового клиента устанавливаем company_id текущего пользователя
        setFormData((prev) => ({
          ...prev,
          company_id: currentUser.company_id,
        }));
      }
      return;
    }

    try {
      setLoading(true);
      const response = await api.get(`/clients/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const clientData = response.data;
      setFormData({
        // Основные поля с fallback на пустую строку
        name: clientData.name || "",
        contact_info: clientData.contact_info || "",
        debt: Number(clientData.debt) || 0,
        black_list: clientData.black_list || false,
        attraction_source_id: clientData.attraction_source_id || "",
        assistant_status: clientData.assistant_status ?? 1,

        // Тип клиента
        client_type: clientData.client_type || "individual",
        first_name: clientData.first_name || "",
        last_name: clientData.last_name || "",
        middle_name: clientData.middle_name || "",
        birth_date: dateOnly(clientData.birth_date),
        gender: clientData.gender || "",
        comment: clientData.comment || "",

        // Паспортные данные
        passport_series: clientData.passport_series || "",
        passport_number: clientData.passport_number || "",
        passport_issued_by: clientData.passport_issued_by || "",
        passport_issued_date: dateOnly(clientData.passport_issued_date),
        passport_division_code: clientData.passport_division_code || "",
        inn: clientData.inn || "",

        // Дополнительные флаги
        no_contract: clientData.no_contract || false,
        no_deposit: clientData.no_deposit || false,
        sms_notifications: clientData.sms_notifications !== false, // По умолчанию true

        // Контакты и адрес
        phone: clientData.phone || "",
        email: clientData.email || "",
        address: clientData.address || "",

        // Скидки
        discount_percent: Number(clientData.discount_percent) || 0,
      });
    } catch (error) {
      console.error("Ошибка загрузки клиента:", error);
      enqueueSnackbar("Ошибка загрузки клиента", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  // Функция для загрузки компаний (заглушка, так как нет endpoints для компаний)
  const fetchCompanies = async () => {
    // Пока что создадим статический список
    setCompanies([
      { id: 1, name: "Компания 1" },
      { id: 2, name: "Компания 2" },
      { id: 3, name: "Компания 3" },
    ]);
  };

  // Функция для загрузки источников привлечения
  const fetchAttractionSources = async () => {
    try {
      const response = await api.get("/clients/attraction-sources/list", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setAttractionSources(response.data);
    } catch (error) {
      console.error("Ошибка загрузки источников привлечения:", error);
      enqueueSnackbar("Ошибка загрузки источников привлечения", {
        variant: "error",
      });
    }
  };

  // Создание нового источника привлечения
  const handleCreateAttractionSource = async () => {
    if (!newSourceName.trim()) {
      enqueueSnackbar("Введите название источника", { variant: "error" });
      return;
    }

    setSourceSaving(true);
    try {
      const response = await api.post(
        "/attraction-sources/",
        {
          name: newSourceName.trim(),
          description: newSourceDescription.trim() || null,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      // Обновляем список источников
      setAttractionSources((prev) => [...prev, response.data]);

      // Автоматически выбираем новый источник
      setFormData((prev) => ({
        ...prev,
        attraction_source_id: response.data.id,
      }));

      // Закрываем диалог и очищаем форму
      setSourceDialogOpen(false);
      setNewSourceName("");
      setNewSourceDescription("");

      enqueueSnackbar("Источник привлечения создан", { variant: "success" });
    } catch (error) {
      console.error("Ошибка создания источника привлечения:", error);
      enqueueSnackbar("Ошибка создания источника привлечения", {
        variant: "error",
      });
    } finally {
      setSourceSaving(false);
    }
  };

  // Функция для получения информации о текущем пользователе
  const fetchCurrentUser = async () => {
    try {
      const response = await api.get("/login/me", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Ошибка получения информации о пользователе:", error);
      // В случае ошибки возвращаем дефолтные значения
      return { company_id: 2, user_id: 7 };
    }
  };

  // Валидация данных клиента
  const validateForm = () => {
    const newErrors = {};

    // Обязательные поля
    if (!formData.name?.trim() && !formData.first_name?.trim()) {
      newErrors[formData.client_type === 'individual' ? 'first_name' : 'name'] = "Укажите имя или название клиента";
    }

    if (!formData.contact_info?.trim()) {
      newErrors.contact_info = "Контактная информация обязательна";
    }

    // Валидация номера телефона — строго 11 цифр в формате 7XXXXXXXXXX
    if (formData.contact_info) {
      const digits = formData.contact_info.replace(/\D/g, "");
      const normalized =
        digits.startsWith("8") && digits.length === 11
          ? "7" + digits.slice(1)
          : digits.length === 10
            ? "7" + digits
            : digits;

      if (!/^7\d{10}$/.test(normalized)) {
        newErrors.contact_info =
          "Некорректный номер телефона. Ожидается 11 цифр (например: 79171234567)";
      }
    }

    // Валидация дополнительного телефона (если указан)
    if (formData.phone?.trim()) {
      const phoneDigits = formData.phone.replace(/\D/g, "");
      const phoneNormalized =
        phoneDigits.startsWith("8") && phoneDigits.length === 11
          ? "7" + phoneDigits.slice(1)
          : phoneDigits.length === 10
            ? "7" + phoneDigits
            : phoneDigits;

      if (!/^7\d{10}$/.test(phoneNormalized)) {
        newErrors.phone =
          "Некорректный доп. телефон. Ожидается 11 цифр (например: 79171234567)";
      }
    }

    // Валидация суммы долга
    if (formData.debt < 0) {
      newErrors.debt = "Сумма долга не может быть отрицательной";
    }

    // Валидация скидки
    if (formData.discount_percent < 0 || formData.discount_percent > 100) {
      newErrors.discount_percent = "Скидка должна быть от 0 до 100%";
    }

    // Валидация ИНН (если указан)
    if (formData.inn && !/^\d{10,12}$/.test(formData.inn)) {
      newErrors.inn = "ИНН должен содержать 10-12 цифр";
    }

    // Валидация паспорта
    if (formData.passport_series && !/^\d{4}$/.test(formData.passport_series)) {
      newErrors.passport_series = "Серия паспорта должна содержать 4 цифры";
    }

    if (formData.passport_number && !/^\d{6}$/.test(formData.passport_number)) {
      newErrors.passport_number = "Номер паспорта должен содержать 6 цифр";
    }

    // Фильтруем пустые ошибки
    const filteredErrors = Object.fromEntries(
      Object.entries(newErrors).filter(
        ([key, value]) => value && value.trim() !== "",
      ),
    );

    console.log("Результат валидации:", filteredErrors);
    setErrors(filteredErrors);
    return Object.keys(filteredErrors).length === 0;
  };

  // Обработка изменения полей
  const handleChange = (field) => (event) => {
    const value =
      event.target.type === "checkbox"
        ? event.target.checked
        : event.target.value;

    // Обновляем данные
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Убираем ошибку при изменении поля
    setErrors((prev) => {
      // Создаем новый объект без текущего поля ошибки
      const { [field]: removedError, ...newErrors } = prev;
      console.log(
        `Удалена ошибка для поля ${field}, оставшиеся ошибки:`,
        newErrors,
      );
      console.log(
        `Количество оставшихся ошибок: ${Object.keys(newErrors).length}`,
      );
      return newErrors;
    });
  };

  // Функция сохранения
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (savingRef.current) return;

    if (!validateForm()) {
      enqueueSnackbar("Пожалуйста, исправьте ошибки в форме", {
        variant: "error",
      });
      return;
    }

    savingRef.current = true;
    setSaving(true);
    try {
      const submitData = {
        ...formData,
        debt: Number(formData.debt) || 0,
        discount_percent: Number(formData.discount_percent) || 0,
        // Преобразуем пустые строки в null для дат
        birth_date: formData.birth_date || null,
        gender: formData.gender || null,
        passport_issued_date: formData.passport_issued_date || null,
        // Преобразуем пустую строку attraction_source_id в null
        attraction_source_id:
          formData.attraction_source_id === ""
            ? null
            : Number(formData.attraction_source_id) || null,
      };

      let createdClientId = null;
      let createdClientData = null;

      if (isNewClient) {
        // Создание нового клиента
        const response = await api.post("/clients/", submitData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        });
        createdClientId = response.data?.id;
        createdClientData = response.data;
        enqueueSnackbar("Клиент успешно создан", { variant: "success" });
      } else {
        // Обновление существующего клиента
        await api.put(`/clients/${id}`, submitData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        });
        enqueueSnackbar("Клиент успешно обновлен", { variant: "success" });
      }

      // Определяем куда перенаправить после сохранения
      // Проверяем, пришли ли из формы сделки (путь содержит rental-deals)
      if (returnTo && returnTo.includes("rental-deals")) {
        // Если пришли из формы аренды, отправляем сообщение родительской вкладке
        if (window.opener) {
          window.opener.postMessage(
            {
              type: "CLIENT_CREATED",
              clientId: createdClientId,
              clientData: createdClientData,
            },
            window.location.origin,
          );
        }
        enqueueSnackbar(
          isNewClient
            ? "Клиент создан! Закрываем окно..."
            : "Клиент обновлен! Закрываем окно...",
          { variant: "success" },
        );

        // Закрываем окно через небольшую задержку, чтобы пользователь увидел сообщение
        setTimeout(() => {
          window.close();
        }, 1500);
      } else {
        // Возвращаемся назад (туда откуда пришли)
        navigate(-1);
      }
    } catch (error) {
      // Логируем только серверные ошибки (5xx), валидация обрабатывается через snackbar
      if (!error.response || error.response.status >= 500) {
        console.error("Ошибка сохранения клиента:", error);
      }

      let errorMessage = "Ошибка сохранения клиента";

      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          // Если это массив ошибок валидации
          const validationErrors = error.response.data.detail
            .map((err) => `${err.loc?.join(".")}: ${err.msg}`)
            .join(", ");
          errorMessage = `Ошибка валидации: ${validationErrors}`;
        } else {
          errorMessage = error.response.data.detail;
        }
      }

      enqueueSnackbar(errorMessage, {
        variant: "error",
        autoHideDuration: 6000,
      });
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  // Функция отмены
  const handleCancel = () => {
    if (returnTo === "rental-deals") {
      window.close(); // Закрываем вкладку если пришли из аренды
    } else {
      navigate(-1); // Возвращаемся назад (туда откуда пришли)
    }
  };

  useEffect(() => {
    fetchClient();
    fetchCompanies();
    fetchAttractionSources();
  }, [id]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <Typography>Загрузка...</Typography>
      </Box>
    );
  }

  return (
    <Box className="erp-editor erp-client-editor" sx={{ padding: 3 }}>
      <Box sx={{ mb: 1.5, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleCancel}
          variant="outlined"
        >
          Назад
        </Button>
        <Typography variant="h4">
          {isNewClient ? "Создание клиента" : "Редактирование клиента"}
        </Typography>
        {!isNewClient && <Button type="button" variant="outlined" startIcon={<ChatIcon />}
          disabled={chatOpening || saving} onClick={() => openChat({ clientId: id })}>
          {chatOpening ? 'Открываем чат…' : 'Чат с клиентом'}
        </Button>}
      </Box>

      {chatError && <Alert severity="error" sx={{ mb: 2 }}>{chatError}</Alert>}
      {errors.submit && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {errors.submit}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
          {/* Кнопки действий */}
          <Box className="erp-client-savebar">
            <Box className="erp-savebar" sx={{ display: "flex", gap: 2, justifyContent: "flex-end", bgcolor: "white", borderRadius: 2 }}>
              <Button variant="outlined" onClick={handleCancel}>
                Отмена
              </Button>
              <Button
                type="submit"
                disabled={saving}
                variant="contained"
                startIcon={<SaveIcon />}
              >
                Сохранить
              </Button>

            </Box>
          </Box>
        <Grid container spacing={2} className="erp-client-sections">
          {/* Основная информация */}
          <Grid item xs={12} className="erp-client-identity">
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Основная информация
                </Typography>
                <Grid container spacing={2}>


                  {formData.client_type === "individual" ? (
                    <>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Фамилия *"
                          value={formData.last_name}
                          onChange={handleChange("last_name")}
                          error={!!errors.last_name}
                          helperText={errors.last_name}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Имя *"
                          value={formData.first_name}
                          onChange={handleChange("first_name")}
                          error={!!errors.first_name}
                          helperText={errors.first_name}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Отчество"
                          value={formData.middle_name}
                          onChange={handleChange("middle_name")}
                        />
                      </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Тип клиента</InputLabel>
                      <Select
                        value={formData.client_type || ""}
                        onChange={handleChange("client_type")}
                        label="Тип клиента"
                      >
                        <MenuItem value="individual">Физическое лицо</MenuItem>
                        <MenuItem value="legal">Юридическое лицо</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Дата рождения"
                          type="date"
                          value={formData.birth_date}
                          onChange={handleChange("birth_date")}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          select
                          size="small"
                          fullWidth
                          label="Пол"
                          value={formData.gender || ""}
                          onChange={handleChange("gender")}
                        >
                          <MenuItem value="">Не указан</MenuItem>
                          <MenuItem value="male">Мужской</MenuItem>
                          <MenuItem value="female">Женский</MenuItem>
                        </TextField>
                      </Grid>
                    </>
                  ) : (
                    <>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Название организации *"
                        value={formData.name}
                        onChange={handleChange("name")}
                        error={!!errors.name}
                        helperText={errors.name}
                      />
                    </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Тип клиента</InputLabel>
                      <Select
                        value={formData.client_type || ""}
                        onChange={handleChange("client_type")}
                        label="Тип клиента"
                      >
                        <MenuItem value="individual">Физическое лицо</MenuItem>
                        <MenuItem value="legal">Юридическое лицо</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                    </>
                  )}

                  <Grid item xs={12} sm={6} md={4}>
                    <Box
                      sx={{ display: "flex", alignItems: "stretch", gap: 1 }}
                    >
                      <FormControl fullWidth size="small">
                        <InputLabel>Источник привлечения</InputLabel>
                        <Select
                          value={formData.attraction_source_id || ""}
                          onChange={handleChange("attraction_source_id")}
                          label="Источник привлечения"
                        >
                          <MenuItem value="">Выберите источник</MenuItem>
                          {attractionSources.map((source) => (
                            <MenuItem key={source.id} value={source.id}>
                              {source.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <IconButton
                        color="primary"
                        onClick={() => setSourceDialogOpen(true)}
                        sx={{
                          border: "1px solid #ccc",
                          borderRadius: 1,
                          height: "40px",
                          width: "40px",
                        }}
                        title="Добавить новый источник привлечения"
                      >
                        <AddIcon />
                      </IconButton>
                    </Box>
                  </Grid>


                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Паспортные данные */}
          {formData.client_type === "individual" && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Паспортные данные
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Серия"
                        value={formData.passport_series}
                        onChange={handleChange("passport_series")}
                        error={!!errors.passport_series}
                        helperText={errors.passport_series}
                        inputProps={{ maxLength: 4 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Номер"
                        value={formData.passport_number}
                        onChange={handleChange("passport_number")}
                        error={!!errors.passport_number}
                        helperText={errors.passport_number}
                        inputProps={{ maxLength: 6 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Где выдан"
                        value={formData.passport_issued_by}
                        onChange={handleChange("passport_issued_by")}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Дата выдачи"
                        type="date"
                        value={formData.passport_issued_date}
                        onChange={handleChange("passport_issued_date")}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Код подразделения"
                        value={formData.passport_division_code}
                        onChange={handleChange("passport_division_code")}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        size="small"
                        fullWidth
                        label="ИНН"
                        value={formData.inn}
                        onChange={handleChange("inn")}
                        error={!!errors.inn}
                        helperText={errors.inn}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Контакты */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Контакты
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Основной контакт *"
                      value={formData.contact_info}
                      onChange={handleChange("contact_info")}
                      error={!!errors.contact_info}
                      helperText={errors.contact_info}
                      placeholder="+7 (900) 123-45-67"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Дополнительный телефон"
                      error={!!errors.phone}
                      helperText={errors.phone}
                      value={formData.phone}
                      onChange={handleChange("phone")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange("email")}
                      placeholder="example@domain.com"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Адрес"
                      multiline
                      minRows={1}
                      maxRows={3}
                      value={formData.address}
                      onChange={handleChange("address")}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Настройки */}
          <Grid item xs={12} className="erp-client-settings">
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Настройки
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Stack spacing={1}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.black_list}
                            onChange={handleChange("black_list")}
                          />
                        }
                        label="В черном списке"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.no_contract}
                            onChange={handleChange("no_contract")}
                          />
                        }
                        label="Без договора"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.no_deposit}
                            onChange={handleChange("no_deposit")}
                          />
                        }
                        label="Без залога"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.sms_notifications}
                            onChange={handleChange("sms_notifications")}
                          />
                        }
                        label="SMS уведомления"
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Скидка (%)"
                          type="number"
                          value={formData.discount_percent}
                          onChange={handleChange("discount_percent")}
                          error={!!errors.discount_percent}
                          helperText={errors.discount_percent}
                          inputProps={{ min: 0, max: 100, step: 0.1 }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Сумма долга"
                          type="number"
                          value={formData.debt}
                          onChange={handleChange("debt")}
                          error={!!errors.debt}
                          helperText={errors.debt}
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Комментарий"
                      multiline
                      minRows={1}
                      maxRows={4}
                      value={formData.comment}
                      onChange={handleChange("comment")}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* История аренд (только для существующих клиентов) */}
          {!isNewClient && (
            <Grid item xs={12} className="erp-history-section">
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                    История аренд
                  </Typography>
                  <ClientRentalHistory clientId={id} />
                </CardContent>
              </Card>
            </Grid>
          )}


        </Grid>
      </form>

      {/* Диалог создания источника привлечения */}
      <Dialog
        open={sourceDialogOpen}
        onClose={() => setSourceDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Добавить источник привлечения</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                size="small"
                label="Название источника"
                fullWidth
                value={newSourceName}
                onChange={(e) => setNewSourceName(e.target.value)}
                required
                autoFocus
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size="small"
                label="Описание"
                fullWidth
                multiline
                rows={3}
                value={newSourceDescription}
                onChange={(e) => setNewSourceDescription(e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSourceDialogOpen(false)}
            disabled={sourceSaving}
          >
            Отмена
          </Button>
          <Button
            onClick={handleCreateAttractionSource}
            variant="contained"
            disabled={sourceSaving || !newSourceName.trim()}
          >
            {sourceSaving ? "Создание..." : "Создать"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientsEditPage;
